'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface BookingFormProps {
  currentLang?: 'sl' | 'en';
}

export default function BookingForm({ currentLang = 'sl' }: BookingFormProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const supabase = createClient();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const labels = {
    name: currentLang === 'sl' ? 'Ime in priimek' : 'Full Name',
    email: currentLang === 'sl' ? 'Email naslov' : 'Email Address',
    phone: currentLang === 'sl' ? 'Telefonska številka' : 'Phone Number',
    date: currentLang === 'sl' ? 'Želeni datum' : 'Preferred Date',
    message: currentLang === 'sl' ? 'Sporočilo (opcijsko)' : 'Message (optional)',
    submit: currentLang === 'sl' ? 'Pošlji rezervacijo' : 'Submit Booking',
    success: currentLang === 'sl' ?'Hvala! Vaša rezervacija je bila poslana. Kontaktirali vas bomo v 24 urah.' :'Thank you! Your booking has been sent. We will contact you within 24 hours.',
    sending: currentLang === 'sl' ? 'Pošiljanje …' : 'Sending…',
    sendError:
      currentLang === 'sl'
        ? 'Pošiljanje ni uspelo. Poskusite znova ali nas pokličite.'
        : 'Sending failed. Please try again or give us a call.',
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.length < 2) {
      newErrors.name = currentLang === 'sl' ? 'Vnesite veljavno ime' : 'Enter a valid name';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      newErrors.email = currentLang === 'sl' ? 'Vnesite veljaven email' : 'Enter a valid email';
    }

    const phoneRegex = /^[0-9+\s()-]{8,}$/;
    if (!formData.phone || !phoneRegex.test(formData.phone)) {
      newErrors.phone = currentLang === 'sl' ? 'Vnesite veljavno telefonsko številko' : 'Enter a valid phone number';
    }

    if (!formData.date) {
      newErrors.date = currentLang === 'sl' ? 'Izberite datum' : 'Select a date';
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.date = currentLang === 'sl' ? 'Datum mora biti v prihodnosti' : 'Date must be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || isSending) return;

    setIsSending(true);
    setSubmitError('');
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formType: 'booking', ...formData }),
      });
      if (!response.ok) throw new Error(`Email request failed: ${response.status}`);

      // Shrani še v admin panel; neuspeh tu ne sme preprečiti oddaje obrazca.
      const { error } = await supabase.from('booking_submissions').insert([
        {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            date: formData.date,
            message: formData.message || null,
        },
      ]);
      if (error) console.error('Error saving submission:', error);

      setIsSubmitted(true);
      setTimeout(() => {
        setFormData({ name: '', email: '', phone: '', date: '', message: '' });
        setIsSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(labels.sendError);
    } finally {
      setIsSending(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-card card-rounded p-8 shadow-lg space-y-6">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 w-24 bg-muted rounded animate-pulse"></div>
            <div className="h-12 bg-muted rounded animate-pulse"></div>
          </div>
        ))}
        <div className="h-12 bg-muted rounded animate-pulse"></div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="bg-success/10 border border-success card-rounded p-8 text-center space-y-4 animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto">
          <Icon name="CheckCircleIcon" size={32} className="text-success" />
        </div>
        <p className="text-lg font-medium">{labels.success}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card card-rounded p-8 shadow-lg space-y-6">
      {/* Name */}
      <div className="space-y-2">
        <label htmlFor="name" className="text-sm font-medium">
          {labels.name}
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary transition-all ${
            errors.name ? 'border-error' : 'border-input'
          }`}
        />
        {errors.name && <p className="text-sm text-error">{errors.name}</p>}
      </div>

      {/* Email */}
      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium">
          {labels.email}
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary transition-all ${
            errors.email ? 'border-error' : 'border-input'
          }`}
        />
        {errors.email && <p className="text-sm text-error">{errors.email}</p>}
      </div>

      {/* Phone */}
      <div className="space-y-2">
        <label htmlFor="phone" className="text-sm font-medium">
          {labels.phone}
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary transition-all ${
            errors.phone ? 'border-error' : 'border-input'
          }`}
        />
        {errors.phone && <p className="text-sm text-error">{errors.phone}</p>}
      </div>

      {/* Date */}
      <div className="space-y-2">
        <label htmlFor="date" className="text-sm font-medium">
          {labels.date}
        </label>
        <input
          type="date"
          id="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          className={`w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary transition-all ${
            errors.date ? 'border-error' : 'border-input'
          }`}
        />
        {errors.date && <p className="text-sm text-error">{errors.date}</p>}
      </div>

      {/* Message */}
      <div className="space-y-2">
        <label htmlFor="message" className="text-sm font-medium">
          {labels.message}
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={4}
          className="w-full border border-input rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary transition-all resize-none"
        ></textarea>
      </div>

      {/* Submit Button */}
      {submitError && <p className="text-sm text-error">{submitError}</p>}

      <button
        type="submit"
        disabled={isSending}
        className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSending ? labels.sending : labels.submit}
      </button>
    </form>
  );
}